import { classSet } from '@platform/ui-library';
import { FC, useEffect, useState } from 'react';
import { useSubject } from '@app/hooks';
import {
  LoadingProgress,
  LoadingProgressStatusPhase,
  TimeInMs
} from '@app/types';
import styles from './UploadProgress.module.scss';

export type UploadProgressProps = (
  LoadingProgress
  & {
    classNames?: {
      container?: string;
    };
  }
);

/**
 * Render a progress bar + ETA readout driven by a caller-owned BehaviorSubject.
 *
 * The loading modal wires this up when the active @see LoadingMessageConfig
 * supplies a `progress` subject — scoping progress to the specific loading
 * operation rather than the whole service.
 */
export const UploadProgress: FC<UploadProgressProps> = ({
  classNames,
  label,
  startedAt,
  subject
}) => {
  const progress = useSubject(subject, subject.value);

  // Tick once a second so the ETA keeps counting down even when there is no
  // incoming progress event — the server-side finalize phase can be silent.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!progress) {
      return;
    }
    const handle = window.setInterval(() => setTick((value) => value + 1), TimeInMs.second);
    return () => window.clearInterval(handle);
  }, [progress, startedAt]);

  if (!progress) {
    return null;
  }

  const {
    eta,
    loadedBytes,
    percent,
    primaryLabel
  } = (() => {
    const { phase } = progress;
    const primaryLabel = `${phase} ${label}`;
    if (phase !== LoadingProgressStatusPhase.uploading) {
      return { primaryLabel };
    }
    const { loaded, total } = progress;
    if (total <= 0 || loaded > total) {
      return { primaryLabel };
    }

    const percent = Math.min(100, Math.round((loaded / total) * 100));
    const elapsedSeconds = Math.max(0, (Date.now() - startedAt) / 1000);
    const rateBytesPerSecond = (elapsedSeconds > 0) ? (loaded / elapsedSeconds) : 0;
    const remainingBytes = Math.max(0, total - loaded);
    const etaSeconds = (rateBytesPerSecond <= 0) ? null : (
      Math.ceil(remainingBytes / rateBytesPerSecond)
    );

    return {
      eta: (!etaSeconds) ? null : `${formatDuration(etaSeconds)} remaining`,
      percent,
      loadedBytes: `${formatBytes(loaded)} / ${formatBytes(total)}`,
      primaryLabel: `${primaryLabel} ${percent}%`
    };
  })();

  return (
    <div className={classNames?.container}>
      <div className={styles.bar}>
        <div
          className={classSet([styles.fill, percent == null && styles.fillIndeterminate])}
          style={(percent != null) ? { width: `${percent}%` } : undefined}
        />
      </div>
      <div className={styles.meta}>
        <span>{primaryLabel}</span>
        <span>{loadedBytes}</span>
        <span className={styles.eta}>{eta}</span>
      </div>
    </div>
  );
};

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB'];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  let unitIndex = 0;
  let value = bytes;
  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const precision = (value >= 10 || unitIndex === 0) ? 0 : 1;
  return `${value.toFixed(precision)} ${BYTE_UNITS[unitIndex]}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 1) {
    return 'less than a second';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return (remainingSeconds === 0) ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return (remainingMinutes === 0) ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
}
